//高鐵搜尋區
import React, { useState, useEffect, forwardRef, useRef, useImperativeHandle } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// TS type
import { THSRSearchOption, TTripType } from '@redux/common';
// Redux
import { RootState } from '@redux/store';

// import module
import StRcln from '@magaele/st_rcln/components/forSearchPanel';

// component
import CalendarM from './CalendarM';
import CalendarPC from './CalendarPC';
import SeatPickerPC from './SeatPickerPC';
import SeatPickerM from './SeatPickerM';

// icon component

// shared component
import Reload from '@svg/Reload';

// common function
import { getTHSRTripInfo } from '@libs/core';
import { getTHSRDetail, getTHSRInfo } from '@libs/apiCall';
import { ReactNode } from 'hoist-non-react-statics/node_modules/@types/react';
import { isMobile } from '@libs/globalVariables';

// plugin
import dayjs from 'dayjs';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

// redux actions
import {
    setTHSRSearchOption,
    setTHSRDetailData,
    setTHSRReturnTrain,
    setTHSRDepartTrain,
    setTHSRDetailLoading,
    setTHSRInfo,
    setTHSRTempTrainItems,
} from '@redux/THSR/actions';

interface ITHSRSearchProps {
    THSRTripType?: string;
    ref?: ReactNode;
}

const THSRSearch: React.FC<ITHSRSearchProps> = forwardRef((props, ref) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const store_THSR = useSelector((state: RootState) => state.THSR);
    const { ETID } = useSelector((state: RootState) => state.Common);

    const [searchOpt, setSearchOpt] = useState<THSRSearchOption>(null);
    const [searchBtnDisable, setSearchBtnDisable] = useState<boolean>(false);
    const [departureErr, setDepartureErr] = useState<Boolean>(false);
    const [destinationErr, setDestinationErr] = useState<Boolean>(false);
    const [seatInfoErr, setSeatInfoErr] = useState<Boolean>(false);
    const [calendarErr, setCalendarErr] = useState<Boolean>(false);
    const [isFirstSearch, setIsFirstSearch] = useState<Boolean>(true);
    const [TPrevStep, setTPrevStep] = useState<number>(0);
    const [research, setResearch] = useState<Boolean>(false);
    const stationsRef = useRef(null);
    const calendarRef = useRef(null);
    const seatPickerRef = useRef(null);
    const controller = useRef<any>(null); //存放 axios 的中斷相關資料
    /* 
    *  The component instance will be extended
    *  with whatever you return from the callback passed
    *  as the second arguments
    */
    useImperativeHandle(ref, () => ({
        researchByNormalClass() {
            //用標準車廂再搜尋一次
            setSearchOpt((prevState) => ({
                ...prevState,
                seatClass: 'Y',
            }));
            setResearch(true);
        },
    }));

    const fetchTHSRDetail = () => {
        dispatch(setTHSRTempTrainItems([]));
        dispatch(setTHSRDepartTrain(null));
        dispatch(setTHSRReturnTrain(null));
        dispatch(setTHSRDetailData(null));
        dispatch(setTHSRInfo(null));
        dispatch(setTHSRDetailLoading(true));
        /*
            TODO:
            1.次日最遠可定日期API
            2.查詢空位班次API
        */
        if (controller.current) {
            controller.current.abort();
        }
        controller.current = new AbortController();
        let signal = controller.current.signal;

        Promise.all([
            getTHSRDetail({
                context: store_THSR,
                req: { ...searchOpt, THSRType: props.THSRTripType, Etcode: ETID },
            }),
            getTHSRInfo({
                signal: signal,
                req: {
                    ...searchOpt,
                    THSRType: props.THSRTripType,
                    Etcode: ETID,
                },
            })(dispatch),
        ]).then((res) => {
            if (!res[0]?.error) {
                dispatch(
                    setTHSRTempTrainItems(
                        res[0]?.Rounds.map((child) => {
                            return child.TrainItems;
                        })
                    )
                );
            }

            dispatch(setTHSRDetailData(res[0]));
            dispatch(setTHSRInfo(res[1]));
            dispatch(setTHSRDetailLoading(false));
        });
    };

    //搜尋
    const submitSearch = () => {
        if (searchOptValidation()) {
            setIsFirstSearch(false);
            //將搜尋條件存進store裡面
            dispatch(setTHSRSearchOption({ ...searchOpt, THSRType: props.THSRTripType }));
            //點擊搜尋後按鈕要變disable的狀態
            setSearchBtnDisable(true);
            //fetch資料回來
            fetchTHSRDetail();
        }
    };

    const sortProductAreaID = (ProductArea) => {
        if (!ProductArea || ProductArea.length < 1) return [];
        let original = [];

        Object.keys(store_THSR.THSRStations.City).forEach((child) => {
            original = original.concat(Object.keys(store_THSR.THSRStations.City[child]));
        });
        const sortedArr = original.filter((child) => ProductArea.indexOf(child) >= 0);
        return sortedArr;
    };

    //過篩出發地目的地的選項
    const filterStations = (type, tripType) => {
        // 先判斷是單程面板還是來回面板
        let currentProductAreaID: Array<string> = getTHSRTripInfo(store_THSR.THSRInitData.ThsrTripInfos, props.THSRTripType).ProductAreaID
        //THSRStations為IT的ThsrStation.json
        const THSRStations = store_THSR.THSRStations;
        //productLocat該產品包含的站別（store）且要由北到南排列
        const productLocat = sortProductAreaID(currentProductAreaID);
        //產品是否為多目的地
        const isMultipleLocat = productLocat.length > 1;

        //根據IT的ThsrStation.json mapping出所有車站
        let allStations = {};
        //otps:所有站mapping出的options
        let opts = [{ text: t('THSR.commonPanel.choose', {particle: t('THSR.commonPanel.please')}), value: '' }];
        // let opts = [{ text: '', value: '' }];

        if (THSRStations) {
            Object.keys(THSRStations.City).forEach((child) => {
                allStations = { ...allStations, ...THSRStations.City[child] };
            });
            Object.keys(allStations).forEach((child) => {
                opts.push({ text: allStations[child] + t('THSR.commonPanel.state'), value: child });
            });
        }
        let productLocatData = {};
        let productLocatOpts = [{ text: t('THSR.commonPanel.choose', {particle: t('THSR.commonPanel.please')}), value: '' }];

        //根據產品所在城市filter出車站
        if (productLocat.length > 0 && THSRStations) {
            productLocat.forEach((child) => {
                // 若產品回傳的是所在城市
                // productLocatData = { ...productLocatData, ...THSRStations.City[child] };
                // 若產品回傳的是所在的高鐵站名
                productLocatData = { ...productLocatData, ...{ [child]: allStations[child] } };
            });
            Object.keys(productLocatData).forEach((child) => {
                productLocatOpts.push({
                    text: allStations[child] + t('THSR.commonPanel.state'),
                    value: child,
                });
            });
        }
        //單程-出發地的選項
        //出發地選擇後目的地移除該選項
        if (type === 'departure' && searchOpt?.destination && tripType === TTripType.oneway) {
            // if (productLocatData[searchOpt?.destination]) {
                //目的地選到產品所在地則產出全部站
                // opts = opts;
            // } else {
                //目的地未選產品所在地則產出產品所在地
                // opts = Object.keys(productLocatData).length > 0 ? productLocatOpts : opts;
                // opts = opts;
            // }
            // //目的地選擇後出發地移除該選項
            // opts = opts.filter((child) => child.value !== searchOpt?.destination);
            opts = opts;
        }

        //單程-目的地的選項
        if (type === 'destination' && searchOpt?.departure && tripType === TTripType.oneway) {
            if (productLocatData[searchOpt?.departure]) {
                //出發地選到產品所在地則產出全部站
                opts = opts;
            } else {
                //出發地未選產品所在地則產出產品所在地
                opts = Object.keys(productLocatData).length > 0 ? productLocatOpts : opts;
            }
            //出發地選擇後目的地移除該選項
            opts = opts.filter((child) => child.value !== searchOpt?.departure);
        }

        //來回-出發地的選項
        if (type === 'departure' && tripType === TTripType.round) {
            if (isMultipleLocat) {
                //若產品為多點 opts產全部高鐵站
                opts = opts;
            } else {
                //若產品為單點
                opts = opts.filter((child) => !productLocatData[child.value]);
            }
            //出發地移除產品所在地
            // opts = opts.filter((child) => child.value !== searchOpt?.destination);
        }
        //來回-目的地的選項
        if (type === 'destination' && searchOpt?.departure && tripType === TTripType.round) {
            //來回的目的地選擇僅有產品所在地（無多/單點的差異）
            opts = productLocatOpts;
            opts = opts.filter((child) => child.value !== searchOpt?.departure);
        }
        return opts;
    };

    //更換起末站
    const whenStationsChange = (v, type) => {
        console.log('v', v,"searchOpt",searchOpt)
        // 先判斷是單程面板還是來回面板
        let currentProductAreaID: Array<string> = getTHSRTripInfo(store_THSR.THSRInitData.ThsrTripInfos, props.THSRTripType).ProductAreaID
        
        //避免手機版開啟下拉時就觸發change
        if (v !== searchOpt[type]) {
            if (type === 'departure') {
                console.log("有近來")
                /*
                    當產品所在地只有一個的時候（store_THSR.THSRInitData.ProductAreaID），
                    選擇完出發站就要把所在地帶入目的地站
                */
                if (
                    currentProductAreaID &&
                    currentProductAreaID.length === 1 &&
                    !currentProductAreaID.includes(v)
                ) {
                    setSearchOpt((prevState) => ({
                        ...prevState,
                        [type]: v,
                        destination: currentProductAreaID[0],
                    }));
                } else {
                    setSearchOpt((prevState) => ({
                        ...prevState,
                        [type]: v,
                        destination: '',
                    }));
                }
            } else {
                setSearchOpt((prevState) => ({
                    ...prevState,
                    [type]: v,
                }));
            }
        }

        if (v !== '') {
            switch (type) {
                case 'departure':
                    setDepartureErr(false);
                    break;
                case 'destination':
                    setDestinationErr(false);
                    break;
                default:
                    break;
            }
        }
    };

    //更改日期and時間
    const whenDatesChange = (data) => {
        setCalendarErr(false);
        // console.log('更改日期and時間');
        setSearchOpt((prevState) => ({
            ...prevState,
            ...data,
        }));
    };

    //更改艙等票種人數
    const whenSeatsChange = (data) => {
        setSeatInfoErr(false);
        setSearchOpt((prevState) => ({
            ...prevState,
            ...data,
        }));
    };
    //檢查搜尋條件是否都有設定，若已完成設定責需要在change的時候就call 班次查詢的api
    const searchOptValidation = () => {
        let check = true;
        if (!searchOpt['startDate'] || !searchOpt['startTime']) {
            setCalendarErr(true);
            //出發日期或出發時間錯誤
            // calendarRef.current?.classList.add('errorShake');
            // setTimeout(() => {
            //     calendarRef.current?.classList.remove('errorShake');
            // }, 1000);
            check = false;
        }
        if (
            props.THSRTripType === TTripType.round &&
            (!searchOpt['endDate'] || !searchOpt['endTime'])
        ) {
            setCalendarErr(true);
            //結束日期或結束時間錯誤
            // calendarRef.current?.classList.add('errorShake');
            // setTimeout(() => {
            //     calendarRef.current?.classList.remove('errorShake');
            // }, 1000);
            check = false;
        }

        if (
            props.THSRTripType === TTripType.round &&
            searchOpt['startDate'] &&
            searchOpt['startTime'] &&
            searchOpt['endDate'] &&
            searchOpt['endTime']
        ) {
            if (
                dayjs(searchOpt['startDate'] + searchOpt['startTime']).isAfter(
                    searchOpt['endDate'] + searchOpt['endTime']
                )
            ) {
                setCalendarErr(true);
                //結束日期或結束時間錯誤
                // calendarRef.current?.classList.add('errorShake');
                // setTimeout(() => {
                //     calendarRef.current?.classList.remove('errorShake');
                // }, 1000);
                check = false;
            }
        }
        if (!searchOpt['departure']) {
            // 出發站錯誤
            // stationsRef.current?.classList.add('errorShake');
            // setTimeout(() => {
            //     stationsRef.current?.classList.remove('errorShake');
            // }, 1000);
            setDepartureErr(true);
            check = false;
        }
        if (!searchOpt['destination']) {
            // 目的站錯誤
            // stationsRef.current?.classList.add('errorShake');
            // setTimeout(() => {
            //     stationsRef.current?.classList.remove('errorShake');
            // }, 1000);
            setDestinationErr(true);
            check = false;
        }
        if (!searchOpt['seatClass']) {
            // 車廂錯誤
            // seatPickerRef.current?.classList.add('errorShake');
            // setTimeout(() => {
            //     seatPickerRef.current?.classList.remove('errorShake');
            // }, 1000);
            setSeatInfoErr(true);
            check = false;
        }
        if (
            !searchOpt['seatInfo'] ||
            Object.values(searchOpt['seatInfo']).reduce((a, b) => a + b) === 0
        ) {
            //人數錯誤
            // seatPickerRef.current?.classList.add('errorShake');
            // setTimeout(() => {
            //     seatPickerRef.current?.classList.remove('errorShake');
            // }, 1000);
            setSeatInfoErr(true);
            check = false;
        }
        return check;
    };

    //當搜尋條件有變動的時候，按鈕要取消反灰
    const checkSearchOptionChanged = () => {
        Object.keys(store_THSR.THSRSearchOption).forEach((key) => {
            if (
                searchBtnDisable &&
                JSON.stringify(searchOpt[key]) !==
                    JSON.stringify(store_THSR.THSRSearchOption[key]) &&
                key !== 'THSRType'
            ) {
                setSearchBtnDisable(false);
            }
        });
    };

    const noBusinessClass = () => {
        //南港、台北、板橋不賣商務艙
        let noBusiness = false;
        if (searchOpt?.departure && searchOpt?.destination) {
            if (
                ['NAK', 'TPE', 'BAC'].includes(searchOpt?.departure) &&
                ['NAK', 'TPE', 'BAC'].includes(searchOpt?.destination)
            ) {
                noBusiness = true;
            }
        }
        return noBusiness;
    };

    useEffect(() => {
        //搜尋條件是否有變動
        if (searchOpt && store_THSR.THSRSearchOption) {
            checkSearchOptionChanged();
        }
    }, [searchOpt]);

    useEffect(() => {
        //手機版的時候搜尋欄位有變動就要call api
        if (isMobile && searchOpt) {
            submitSearch();
        }

        if (!isMobile && research && searchOpt) {
            setResearch(false);
            submitSearch();
        }
    }, [searchOpt, research]);

    //從選擇方案返回高鐵面板時要重新load班次
    useEffect(() => {
        if (TPrevStep === 1 && store_THSR.TCurrentStep === 0) {
            submitSearch();
        }
        setTPrevStep(store_THSR.TCurrentStep);
    }, [store_THSR.TCurrentStep]);
    // {t('THSR.commonPanel.choose', {particle: t('THSR.commonPanel.please')})}
    // {t('THSR.commonPanel.state_departure')}
    return (
        <>
            {/*包含人數&票種的搜尋欄位*/}
            <div className="THSRSearch">
                <div className="selectContainer" ref={stationsRef}>
                    <div className="stationSelect">
                        <div className="stationContainer">
                            <StRcln
                                ClassName="departureSelect"
                                disabled={store_THSR.THSRDetailLoading}
                                option={filterStations('departure', props.THSRTripType)}
                                placeholder="請選擇"
                                label={t('THSR.commonPanel.state_departure')}
                                breakline
                                onChangeCallBack={(v) => whenStationsChange(v, 'departure')}
                                defaultValue={searchOpt?.departure ? searchOpt.departure : ''}
                                error={departureErr}
                                dropdownContentClassName={'THSR-dropdown h-f'}
                            ></StRcln>
                        </div>
                        <span className="arrow">→</span>
                        <div className="stationContainer">
                            <StRcln
                                ClassName="destinationSelect"
                                disabled={
                                    !searchOpt?.departure ||
                                    searchOpt?.departure === '' ||
                                    store_THSR.THSRDetailLoading
                                }
                                option={filterStations('destination', props.THSRTripType)}
                                placeholder="請選擇"
                                label={t('THSR.commonPanel.state_destination')}
                                breakline
                                onChangeCallBack={(v) => whenStationsChange(v, 'destination')}
                                defaultValue={searchOpt?.destination ? searchOpt.destination : ''}
                                error={destinationErr}
                                dropdownContentClassName={'THSR-dropdown h-f'}
                            ></StRcln>
                        </div>
                    </div>
                </div>
                <div className="selectContainer" ref={calendarRef}>
                    {isMobile ? (
                        <CalendarM
                            disabled={
                                !searchOpt?.departure ||
                                !searchOpt?.destination ||
                                store_THSR.THSRDetailLoading
                            }
                            error={calendarErr}
                            THSRTripType={props.THSRTripType}
                            whenDatesChange={whenDatesChange}
                        />
                    ) : (
                        <CalendarPC
                            disabled={
                                !searchOpt?.departure ||
                                !searchOpt?.destination ||
                                store_THSR.THSRDetailLoading
                            }
                            error={calendarErr}
                            THSRTripType={props.THSRTripType}
                            whenDatesChange={whenDatesChange}
                        />
                    )}
                </div>
                <div className="selectContainer" ref={seatPickerRef}>
                    {isMobile ? (
                        <SeatPickerM
                            disabled={
                                !searchOpt?.departure ||
                                !searchOpt?.destination ||
                                store_THSR.THSRDetailLoading
                            }
                            disabledSessions={noBusinessClass() && ['J']}
                            error={seatInfoErr}
                            whenSeatsChange={whenSeatsChange}
                            seatClassSelected={searchOpt?.seatClass || ''}
                        />
                    ) : (
                        <SeatPickerPC
                            disabled={
                                !searchOpt?.departure ||
                                !searchOpt?.destination ||
                                store_THSR.THSRDetailLoading
                            }
                            disabledSessions={noBusinessClass() && ['J']}
                            error={seatInfoErr}
                            whenSeatsChange={whenSeatsChange}
                            seatClassSelected={searchOpt?.seatClass || ''}
                        />
                    )}
                </div>
                <div className="trainSearchBtn">
                    <button
                        className={cx({
                            disabled: searchBtnDisable,
                        })}
                        disabled={searchBtnDisable}
                        onClick={() => {
                            submitSearch();
                        }}
                    >
                        {store_THSR.THSRDetailLoading ? (
                            <Reload color={searchBtnDisable ? '#999' : '#fff'} />
                        ) : (
                            t('THSR.commonPanel.search')
                        )}
                    </button>
                    {!isFirstSearch && !searchBtnDisable && (
                        <span className="errMsg">{t('THSR.commonPanel.restartSearch', {symbol: '！'})}</span>
                    )}
                </div>
            </div>
        </>
    );
});

export default THSRSearch;